/* eslint-disable react-refresh/only-export-components */
//Disable because Fast refresh only works when a file only exports components
import { createContext, FC, ReactNode, useState, useContext } from 'react';
import { CoordinatesType, MarkerObjectType } from '../types/dataTypes';
import { defaultCoordinates } from './default';
import { AreaPolygons } from '../api/queries/useIndexPolygons';

interface MapZoomType{
  min: number;
  max: number;
}
const MapContext = createContext<{
  isStreetViewOn: boolean;
  isMapVisible: boolean;
  zoom: MapZoomType;
  mapTypeId: string;
  currentCoordinates: CoordinatesType;
  indexPolygon: AreaPolygons | null;
  soldMarkerObjects: MarkerObjectType[] | null;
  adsMarkerObjects: MarkerObjectType[] | null;
  marketRectangleBounds: google.maps.LatLngBoundsLiteral | null;
  resetMap: (logout?:boolean) => void;
  setMarketRectangleBounds: (bounds: google.maps.LatLngBoundsLiteral) => void;
  setCoordinates: (coordinates: CoordinatesType) => void;
  setIndexPolygon: (polygons: AreaPolygons | null) => void;
  setIsStreetViewOn: (streetView: boolean) => void;
  setIsMapVisible: (visible: boolean) => void;
  setSoldMarkerObjects: (objects: MarkerObjectType[] | null) => void;
  setAdsMarkerObjects: (objects: MarkerObjectType[] | null) => void;
  setMapTypeId: (id:string) => void;
  setZoom: (value:number) => void;
}>({
  mapTypeId: "roadmap",
  zoom: {min: 14,max:16},
  isStreetViewOn: false,
  isMapVisible: true,
  currentCoordinates: defaultCoordinates,
  soldMarkerObjects: null,
  adsMarkerObjects: null,
  marketRectangleBounds: null,
  indexPolygon: null,
  resetMap: () => {},
  setMarketRectangleBounds: () => {},
  setCoordinates: () => {},
  setIndexPolygon: () => {},
  setIsStreetViewOn: () => {},
  setIsMapVisible: () => {},
  setMapTypeId: () => {},
  setAdsMarkerObjects: () => {},
  setSoldMarkerObjects: () => {},
  setZoom: () => {}
});

interface Props {
  children?: ReactNode;
}

export const MapProvider: FC<Props> = ({ children }) => {
  const [isStreetViewOn, setIsStreetViewOn] = useState<boolean>(false);
  const [isMapVisible, setIsMapVisible] = useState<boolean>(true);
  const [mapTypeId,setMapTypeId] = useState<string>("roadmap");
  const [zoom,setZoom] = useState({min: 14,max:15});
  const [currentCoordinates, setCurrentCoordinates] =
    useState<CoordinatesType>(defaultCoordinates);

  const [indexPolygon, setIndexPolygon] = useState<AreaPolygons | null>(null);

  const [soldMarkerObjects, setSoldMarkerObjects] = useState<
    MarkerObjectType[] | null
  >(null);
  const [adsMarkerObjects, setAdsMarkerObjects] = useState<
    MarkerObjectType[] | null
  >(null);
  const [marketRectangleBounds, setMarketRectangleBounds] =
    useState<google.maps.LatLngBoundsLiteral | null>(null);

  const setCoordinates = (coordinates: CoordinatesType) => {
    if (
      coordinates.lat !== currentCoordinates.lat ||
      coordinates.lng !== currentCoordinates.lng
    ) {
      setCurrentCoordinates(coordinates);
    }
  };

  const resetMap = (logout=false) => {
    setCurrentCoordinates(defaultCoordinates);
    setSoldMarkerObjects(null);
    setAdsMarkerObjects(null);
    setMarketRectangleBounds(null);
    if(logout){
      setIndexPolygon(null);
    }
  };

  return (
    <MapContext.Provider
      value={{
        zoom,
        isStreetViewOn,
        isMapVisible,
        mapTypeId,
        currentCoordinates,
        indexPolygon,
        soldMarkerObjects,
        adsMarkerObjects,
        marketRectangleBounds,
        setIndexPolygon,
        resetMap,
        setMarketRectangleBounds,
        setCoordinates,
        setIsStreetViewOn,
        setIsMapVisible,
        setSoldMarkerObjects,
        setAdsMarkerObjects,
        setMapTypeId,
        setZoom
      }}>
      {children}
    </MapContext.Provider>
  );
};

export const useMapContext = () => useContext(MapContext);
